import * as z from "zod/v4";
import { toTag, TAG_REGEXP } from "../util/tag.ts";

export const TagSchema = z
  .string()
  .nonempty()
  .regex(
    TAG_REGEXP,
    "Invalid tag. Tags must be lowercase alphanumeric with underscores only.",
  );

export type Tag = z.infer<typeof TagSchema>;

export const CoerceTagSchema = z.preprocess((value, ctx) => {
  if (!(typeof value === "string")) {
    ctx.addIssue({
      code: "custom",
      message: "Tag must be a string",
    });
    return z.NEVER;
  }
  const tag = toTag(`${value}`);
  if (!tag) {
    ctx.addIssue({
      code: "custom",
      message: "Invalid tag",
    });
    return z.NEVER;
  }
  return tag;
}, TagSchema);

export const TagsSchema = z
  .array(TagSchema)
  .describe(
    `An array of tags to categorize this content. Tag names may use word characters, numbers, and underscores. No spaces or special characters are allowed.`,
  );
export type Tags = z.infer<typeof TagsSchema>;

export const CoerceTagsSchema = z.preprocess(
  (v) =>
    Array.isArray(v)
      ? v.flatMap((t) =>
          typeof t === "string" ? (toTag(t) ? [toTag(t)!] : []) : [],
        )
      : v,
  z.array(z.string()).describe(TagsSchema.description ?? ""),
);

/**
 * Tags without the transform.
 * JSONSchema doesn't support transforms, so we must use plain strings
 * when using tags in a tools context.
 */
export const RawTagsSchema = z
  .array(z.string())
  .describe(
    `An array of tags to categorize this content. Tag names may use word characters, numbers, and underscores. No spaces or special characters are allowed.`,
  );
export type RawTags = z.infer<typeof RawTagsSchema>;
